import { useRef, useMemo, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import styled from "styled-components";

const BackgroundWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  opacity: ${({ darkMode }) => (darkMode ? 0.55 : 0.35)};
`;

function Particles({ count = 400, color }) {
  const ref = useRef();
  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 30;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 30;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 30;
    }
    return pos;
  }, [count]);

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.y = state.clock.elapsedTime * 0.04;
      ref.current.rotation.x = state.clock.elapsedTime * 0.02;
    }
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.04}
        color={color}
        transparent
        opacity={0.7}
        sizeAttenuation
      />
    </points>
  );
}

function FloatingShape({ position, color, speed = 1, size = 0.7 }) {
  const ref = useRef();
  const baseY = position[1];

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.x = state.clock.elapsedTime * 0.25 * speed;
      ref.current.rotation.y = state.clock.elapsedTime * 0.18 * speed;
      ref.current.position.y =
        baseY + Math.sin(state.clock.elapsedTime * speed) * 0.4;
    }
  });

  return (
    <mesh ref={ref} position={position}>
      <icosahedronGeometry args={[size, 0]} />
      <meshBasicMaterial color={color} wireframe transparent opacity={0.35} />
    </mesh>
  );
}

function Scene({ darkMode }) {
  const primary = darkMode ? "#00d4aa" : "#0891b2";
  const accent = darkMode ? "#6366f1" : "#4f46e5";

  return (
    <>
      <ambientLight intensity={0.4} />
      <Particles color={primary} />
      <FloatingShape position={[-4, 2, -3]} color={primary} speed={0.8} size={0.9} />
      <FloatingShape position={[5, -1, -4]} color={accent} speed={1.2} size={0.6} />
      <FloatingShape position={[3, 3, -5]} color={accent} speed={0.6} size={0.5} />
      <FloatingShape position={[-5, -2, -6]} color={primary} speed={1} size={0.75} />
    </>
  );
}

const ThreeBackground = ({ darkMode }) => {
  return (
    <BackgroundWrapper darkMode={darkMode}>
      <Canvas
        camera={{ position: [0, 0, 8], fov: 60 }}
        dpr={[1, 1.5]}
        gl={{ antialias: false, alpha: true }}
      >
        <Suspense fallback={null}>
          <Scene darkMode={darkMode} />
        </Suspense>
      </Canvas>
    </BackgroundWrapper>
  );
};

export default ThreeBackground;
